/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { BackupRecord, OrdenProduccion, DisposicionProducto, User } from '../types';
import { formatColombiaDateTime } from '../utils/colombiaTime';
import { 
  Database, 
  Download, 
  RefreshCw, 
  Trash2, 
  Clock, 
  Plus, 
  ShieldCheck, 
  AlertTriangle, 
  Search, 
  X, 
  RotateCcw, 
  Lock, 
  CheckCircle2, 
  Layers, 
  ClipboardCheck, 
  Users, 
  ShieldAlert,
  Loader2
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { BackupService } from '../services/backupService';

interface GestorResguardoModuleProps {
  currentUser: User;
  ordenes: OrdenProduccion[];
  disposiciones: DisposicionProducto[];
  users: User[];
  backups: BackupRecord[];
  onGenerateManualBackup: () => void;
  onRestoreCompleted?: () => void;
  onDeleteBackup: (backupId: string) => void;
  onClearAllData: () => void;
  isSyncing?: boolean;
  onForceSync?: () => void;
  isReadOnly?: boolean;
}

type RestoreFlowState = 
  | 'IDLE' 
  | 'DRY_RUN_LOADING' 
  | 'AWAITING_CONFIRMATION' 
  | 'RESTORE_LOADING' 
  | 'SUCCESS' 
  | 'ERROR';

export default function GestorResguardoModule({
  currentUser,
  ordenes,
  disposiciones,
  users,
  backups,
  onGenerateManualBackup,
  onRestoreCompleted,
  onDeleteBackup,
  onClearAllData,
  isSyncing = false,
  onForceSync,
  isReadOnly = false,
}: GestorResguardoModuleProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  
  // Security confirmation input for Total System Reset
  const [resetConfirmationText, setResetConfirmationText] = useState('');
  const [resetError, setResetError] = useState('');
  
  const [successMsg, setSuccessMsg] = useState('');

  // Server-Side Dry-Run and Execute Restore State Machine
  const [restoreState, setRestoreState] = useState<RestoreFlowState>('IDLE');
  const [targetBackup, setTargetBackup] = useState<BackupRecord | null>(null);
  const [dryRunData, setDryRunData] = useState<any | null>(null);
  const [correlationId, setCorrelationId] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [restoreResultData, setRestoreResultData] = useState<any | null>(null);

  // Live Colombia Clock State (GMT-5 / America/Bogota)
  const [colombiaClock, setColombiaClock] = useState(() => formatColombiaDateTime(new Date(), true));

  useEffect(() => {
    const timer = setInterval(() => {
      setColombiaClock(formatColombiaDateTime(new Date(), true));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Check role authorization
  const isAuthorized = 
    currentUser.cargo === 'Director Técnico y de Calidad' ||
    currentUser.cargo === 'Administrador del Sistema' ||
    currentUser.esAdmin === true ||
    currentUser.username.toLowerCase() === 'administrador' ||
    currentUser.username.toUpperCase() === 'JUAN.VALENCIA';

  if (!isAuthorized) {
    return (
      <div className="bg-white rounded-3xl border border-slate-200 p-8 text-center max-w-xl mx-auto space-y-4 shadow-sm">
        <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center mx-auto">
          <Lock className="h-8 w-8" />
        </div>
        <h2 className="text-lg font-sans font-black text-slate-900 uppercase">
          Acceso Restringido al Gestor de Resguardo
        </h2>
        <p className="text-xs text-slate-500 font-sans leading-relaxed">
          Este módulo está reservado exclusivamente para los perfiles de <strong>Director Técnico y de Calidad</strong>, <strong>Administrador del Sistema</strong> y el usuario habilitado <strong>JUAN.VALENCIA</strong>.
        </p>
      </div>
    );
  }

  // Filter backups based on active company
  const activeCompanyId = currentUser?.companyId || 'company-insumedic';
  const companyFilteredBackups = backups.filter(b => {
    const backupCId = b.companyId || 'company-insumedic';
    if (activeCompanyId.startsWith('company-insumedic') && backupCId.startsWith('company-insumedic')) {
      return true;
    }
    return backupCId === activeCompanyId;
  });

  const filteredBackups = companyFilteredBackups.filter(b => 
    b.fechaHora.includes(searchTerm) || 
    b.tipo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDownloadJson = (backup: BackupRecord) => {
    const dataStr = JSON.stringify(backup, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `insumedic_backup_${backup.tipo.replace(/[\s\(\):]/g, '_')}_${backup.fechaHora.replace(/[\s:-]/g, '')}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    setSuccessMsg('¡Archivo JSON de Copia de Seguridad descargado exitosamente!');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleStartRestoreDryRun = async (backup: BackupRecord) => {
    if (isReadOnly) {
      alert("Modo Consulta Activo: Licencia vencida. No se pueden restaurar copias de seguridad.");
      return;
    }

    setTargetBackup(backup);
    setRestoreState('DRY_RUN_LOADING');
    setDryRunData(null);
    setErrorCode('');
    setErrorMessage('');
    setRestoreResultData(null);

    const result = await BackupService.dryRunServerRestore(backup, currentUser);

    setCorrelationId(result.correlationId);

    if (!result.success) {
      setRestoreState('ERROR');
      setErrorCode(result.errorCode || 'DRY_RUN_FAILED');
      setErrorMessage(result.error || 'La validación previa server-side no fue satisfactoria.');
      return;
    }

    setDryRunData(result.data);
    setRestoreState('AWAITING_CONFIRMATION');
  };

  const handleConfirmAndExecuteRestore = async () => {
    if (!targetBackup) return;

    setRestoreState('RESTORE_LOADING');

    const result = await BackupService.executeServerRestore(
      targetBackup,
      currentUser,
      'Restauración autorizada por Director Técnico tras validación Server-Side Dry-Run'
    );

    setCorrelationId(result.correlationId);

    if (!result.success) {
      setRestoreState('ERROR');
      setErrorCode(result.errorCode || 'RESTORE_FAILED');
      setErrorMessage(result.error || 'La ejecución de la restauración en el servidor ha fallado.');
      return;
    }

    setRestoreResultData(result.data);
    setRestoreState('SUCCESS');

    // Trigger data reload from server
    if (onRestoreCompleted) {
      onRestoreCompleted();
    } else if (onForceSync) {
      onForceSync();
    }
  };

  const resetRestoreState = () => {
    setRestoreState('IDLE');
    setTargetBackup(null);
    setDryRunData(null);
    setErrorCode('');
    setErrorMessage('');
    setRestoreResultData(null);
  };

  const executeGenerate = () => {
    if (isReadOnly) {
      alert("Modo Consulta Activo: Licencia vencida. No se pueden generar copias de seguridad manuales.");
      return;
    }
    onGenerateManualBackup();
    setSuccessMsg('¡Se ha creado una nueva Copia de Seguridad Manual e inalterable!');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleExecuteTotalReset = () => {
    setResetError('');
    if (resetConfirmationText.trim().toUpperCase() !== 'REESTABLECER') {
      setResetError('Debe escribir exactamente la palabra "REESTABLECER" para confirmar.');
      return;
    }

    // Perform total reset
    onClearAllData();
    setShowResetModal(false);
    setResetConfirmationText('');
    setResetError('');
    
    setSuccessMsg('¡Reestablecimiento Total del Sistema completado con éxito! Todos los datos de fabricación se han reiniciado.');
    setTimeout(() => setSuccessMsg(''), 6000);
  };

  return (
    <div className="space-y-6 font-sans" id="gestor-resguardo-modulo">
      
      {/* Feedback banner */}
      <AnimatePresence>
        {successMsg && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-emerald-50 border border-emerald-250 text-emerald-850 rounded-2xl text-xs font-sans font-bold flex items-center justify-between shadow-xs"
          >
            <div className="flex items-center gap-2.5">
              <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
              <span>{successMsg}</span>
            </div>
            <button onClick={() => setSuccessMsg('')} className="text-emerald-700 hover:text-emerald-900 cursor-pointer">
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Banner & System Metrics */}
      <div className="bg-[#071A36] text-white rounded-3xl p-6 md:p-8 shadow-xl border border-[#1C4F8A]/50 space-y-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-[#1C4F8A]/40 pb-6 relative z-10">
          <div className="flex items-center gap-3.5">
            <div className="p-3 bg-[#1C4F8A] text-sky-300 rounded-2xl border border-[#1C4F8A] shadow-2xs">
              <Database className="h-7 w-7" />
            </div>
            <div>
              <h2 className="text-base md:text-lg font-black tracking-wide uppercase leading-tight text-white">
                GESTOR DE RESGUARDO Y SEGURIDAD DEL SISTEMA
              </h2>
              <p className="text-xs text-slate-200 font-mono mt-1 uppercase tracking-wider flex items-center gap-2 flex-wrap">
                <span>Custodia Inalterable, Copia Diaria a las 18:00 hrs y Auditoría Centralizada</span>
              </p>
              <div className="inline-flex items-center gap-2 mt-2.5 px-3 py-1 bg-blue-500/10 border border-blue-400/30 rounded-xl text-sky-300 text-xs font-mono font-bold shadow-inner">
                <Clock className="h-3.5 w-3.5 text-sky-300 animate-pulse" />
                <span>Hora Oficial Colombia (COT GMT-5): {colombiaClock}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {onForceSync && (
              <button
                onClick={onForceSync}
                disabled={isSyncing}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer shadow-sm ${
                  isSyncing
                    ? 'bg-slate-800 text-slate-400 border-slate-700 animate-pulse'
                    : 'bg-blue-500/10 text-sky-300 hover:bg-blue-500/20 border-blue-500/30'
                }`}
                title="Sincronizar base de datos central en la nube"
              >
                <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                <span>{isSyncing ? 'Sincronizando...' : 'Sincronizar Nube'}</span>
              </button>
            )}

            <button
              onClick={executeGenerate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-slate-950 font-black text-xs rounded-xl transition-all cursor-pointer shadow-md uppercase tracking-wider"
              id="btn-generate-manual-backup-module"
            >
              <Plus className="h-4 w-4" />
              <span>Generar Copia Manual</span>
            </button>
          </div>
        </div>

        {/* 3 Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 relative z-10">
          <div className="bg-[#1C4F8A]/30 border border-[#1C4F8A]/60 rounded-2xl p-4 flex items-center gap-3.5">
            <div className="p-3 bg-sky-500/20 text-sky-300 rounded-xl border border-sky-400/30">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <span className="text-[10px] uppercase font-mono tracking-wider text-slate-300 block">
                Órdenes en Producción
              </span>
              <strong className="text-xl font-mono font-black text-white">
                {ordenes.length}
              </strong>
            </div>
          </div>

          <div className="bg-[#1C4F8A]/30 border border-[#1C4F8A]/60 rounded-2xl p-4 flex items-center gap-3.5">
            <div className="p-3 bg-emerald-500/20 text-emerald-300 rounded-xl border border-emerald-400/30">
              <ClipboardCheck className="h-5 w-5" />
            </div>
            <div>
              <span className="text-[10px] uppercase font-mono tracking-wider text-slate-300 block">
                Disposiciones Liberadas
              </span>
              <strong className="text-xl font-mono font-black text-white">
                {disposiciones.length}
              </strong>
            </div>
          </div>

          <div className="bg-[#1C4F8A]/30 border border-[#1C4F8A]/60 rounded-2xl p-4 flex items-center gap-3.5">
            <div className="p-3 bg-purple-500/20 text-purple-300 rounded-xl border border-purple-400/30">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <span className="text-[10px] uppercase font-mono tracking-wider text-slate-300 block">
                Usuarios Registrados
              </span>
              <strong className="text-xl font-mono font-black text-white">
                {users.length}
              </strong>
            </div>
          </div>
        </div>
      </div>

      {/* Programmed Daily Notice */}
      <div className="bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 rounded-3xl p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
        <div className="flex items-center gap-3.5">
          <div className="p-3 bg-sky-100 text-sky-700 rounded-2xl border border-sky-200 shrink-0">
            <Clock className="h-6 w-6" />
          </div>
          <div className="space-y-0.5">
            <h3 className="font-sans font-black text-slate-900 text-sm uppercase tracking-wide">
              PROGRAMACIÓN AUTOMÁTICA DIARIA: 18:00 HORAS (COT)
            </h3>
            <p className="text-xs text-slate-600">
              El sistema realiza una copia de resguardo inalterable todos los días al cierre de turno. Cada resguardo genera un hash criptográfico SHA-256 inmutable.
            </p>
          </div>
        </div>
      </div>

      {/* History Table */}
      <div className="bg-white rounded-3xl border border-slate-200 p-6 md:p-8 space-y-6 shadow-sm">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="font-sans font-black text-slate-900 text-base uppercase tracking-tight">
              HISTORIAL DE COPIAS DE SEGURIDAD ({filteredBackups.length})
            </h3>
            <p className="text-xs text-slate-500">
              Registro histórico inalterable de puntos de restauración almacenados en la nube.
            </p>
          </div>

          <div className="relative w-full sm:w-72">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por fecha o tipo..."
              className="w-full pl-9 pr-4 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
            />
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-2.5" />
          </div>
        </div>

        {filteredBackups.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
            <Database className="h-8 w-8 text-slate-400 mx-auto mb-2 opacity-50" />
            <p className="text-xs text-slate-500 font-medium">
              No hay registros de copias de seguridad coincidentes.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] font-black uppercase tracking-wider text-slate-500 bg-slate-50/50">
                  <th className="p-3">Fecha y Hora (COT)</th>
                  <th className="p-3">Identificador / SHA-256</th>
                  <th className="p-3">Tipo</th>
                  <th className="p-3 text-center">Órdenes</th>
                  <th className="p-3 text-center">Disposiciones</th>
                  <th className="p-3 text-center">Usuarios</th>
                  <th className="p-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-sans">
                {filteredBackups.map((backup) => (
                  <tr key={backup.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="p-3 text-xs font-bold text-slate-800">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-slate-400" />
                        <span>{backup.fechaHora}</span>
                      </div>
                    </td>
                    <td className="p-3 text-xs">
                      <div className="font-mono text-[11px] font-bold text-slate-700">
                        {backup.id}
                      </div>
                      {backup.hashSha256 && (
                        <div className="text-[10px] font-mono text-emerald-700 flex items-center gap-1 mt-0.5" title={`Hash SHA-256: ${backup.hashSha256}`}>
                          <ShieldCheck className="h-3 w-3 text-emerald-600 shrink-0" />
                          <span>SHA: {backup.hashSha256.substring(0, 10)}...</span>
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        backup.tipo === 'AUTOMATICA (18:00)' 
                          ? 'bg-purple-50 text-purple-800 border border-purple-200' 
                          : 'bg-sky-50 text-sky-800 border border-sky-200'
                      }`}>
                        {backup.tipo}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-center font-mono font-bold text-slate-800">
                      {backup.ordenesCount}
                    </td>
                    <td className="p-3 text-xs text-center font-mono font-bold text-slate-800">
                      {backup.disposicionesCount}
                    </td>
                    <td className="p-3 text-xs text-center font-mono font-bold text-slate-800">
                      {backup.usersCount}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleStartRestoreDryRun(backup)}
                          disabled={restoreState === 'DRY_RUN_LOADING' || restoreState === 'RESTORE_LOADING'}
                          className="px-2.5 py-1 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-slate-950 text-[11px] font-bold rounded-lg transition-colors cursor-pointer inline-flex items-center gap-1 shadow-3xs"
                          title="Restaurar base de datos a este punto mediante validación server-side"
                        >
                          {restoreState === 'DRY_RUN_LOADING' && targetBackup?.id === backup.id ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin text-slate-950" />
                              <span>Validando...</span>
                            </>
                          ) : (
                            <>
                              <RotateCcw className="h-3 w-3" />
                              <span>Restaurar</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleDownloadJson(backup)}
                          className="px-2.5 py-1 bg-slate-800 hover:bg-slate-900 text-white text-[11px] font-bold rounded-lg transition-colors cursor-pointer inline-flex items-center gap-1 shadow-3xs"
                          title="Descargar respaldo en archivo JSON"
                        >
                          <Download className="h-3 w-3" />
                          <span>JSON</span>
                        </button>
                        {currentUser?.username?.toUpperCase() === 'JUAN.VALENCIA' ? (
                          <button
                            onClick={() => onDeleteBackup(backup.id)}
                            className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                            title="Eliminar este registro de copia (SuperAdmin)"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <span className="p-1 text-slate-300 cursor-not-allowed" title="Inmutable: Solo el SuperAdministrador puede purgar resguardos">
                            <Lock className="h-3.5 w-3.5 text-slate-300" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DANGER ZONE: Reestablecimiento Total del Sistema */}
      <div className="bg-rose-50/70 border-2 border-rose-200 rounded-3xl p-6 md:p-8 space-y-4 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-rose-200/80 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-rose-100 text-rose-700 rounded-2xl border border-rose-200">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-sans font-black text-rose-950 text-base uppercase tracking-tight">
                Zona de Seguridad & Reestablecimiento Total del Sistema
              </h3>
              <p className="text-xs text-rose-800/80 font-sans font-medium">
                Acción administrativa de alto nivel para depurar y reiniciar la base de datos de producción.
              </p>
            </div>
          </div>

          <button
            onClick={() => {
              setShowResetModal(true);
              setResetConfirmationText('');
              setResetError('');
            }}
            className="w-full sm:w-auto px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-sans text-xs font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-md hover:shadow-lg inline-flex items-center justify-center gap-2"
            id="btn-open-total-reset-modal"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Reestablecimiento Total del Sistema</span>
          </button>
        </div>

        <p className="text-xs text-rose-900/80 leading-relaxed">
          <strong>Advertencia de Seguridad:</strong> El botón de <strong>Reestablecimiento Total del Sistema</strong> vaciará de forma completa e irreversible todas las órdenes de producción registradas, el historial de disposiciones y los registros de auditoría de fabricación. Reiniciará el consecutivo de órdenes a 1 para dar inicio a un nuevo periodo técnico. <em>Las cuentas de usuarios y la configuración de la empresa NO serán eliminadas.</em>
        </p>
      </div>

      {/* OVERLAY: VALIDACIÓN PREVIA A RESTAURACIÓN (DRY-RUN CONFIRMATION MODAL) */}
      <AnimatePresence>
        {(restoreState === 'AWAITING_CONFIRMATION' || restoreState === 'RESTORE_LOADING') && dryRunData && targetBackup && (
          <div className="fixed inset-0 z-55 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-xs font-sans">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-6 sm:p-7 max-w-lg w-full space-y-5"
            >
              {/* Header */}
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl border border-amber-200">
                  <ShieldAlert className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">
                    Validación Previa a Restauración
                  </h3>
                  <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                    VERIFICACIÓN SERVER-SIDE DRY-RUN
                  </p>
                </div>
                <div className="ml-auto">
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded font-mono text-[9px] font-bold">
                    {correlationId}
                  </span>
                </div>
              </div>

              {/* Pre-Restore Verification Checklist */}
              <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-4 space-y-2 text-xs">
                <span className="block text-[10px] font-black uppercase text-slate-500 font-mono tracking-wider mb-2">
                  Checklist de Seguridad Criptográfica
                </span>

                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="flex items-center gap-1.5 text-slate-700">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>Usuario: <strong className="font-semibold">{currentUser?.nombreCompleto || currentUser?.username}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-700">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>Rol: <strong className="font-semibold">{currentUser?.cargo || 'Director Técnico'}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-700">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>Tenant: <strong className="font-semibold font-mono">{dryRunData.tenantId}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-700">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>Backup ID: <strong className="font-semibold font-mono">{dryRunData.backupId?.substring(0, 14)}...</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-700 col-span-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>SHA-256 íntegro: <strong className="font-mono text-emerald-700">{dryRunData.verifiedSha256?.substring(0, 20)}...</strong></span>
                  </div>
                </div>

                <div className="border-t border-slate-200/80 pt-2 mt-2 grid grid-cols-3 gap-2 text-[10px] text-center font-mono">
                  <div className="bg-white p-2 rounded-lg border border-slate-200">
                    <span className="text-slate-400 block text-[9px]">Órdenes</span>
                    <strong className="text-slate-800 text-xs">{dryRunData.ordenesCount}</strong>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-slate-200">
                    <span className="text-slate-400 block text-[9px]">Disposiciones</span>
                    <strong className="text-slate-800 text-xs">{dryRunData.disposicionesCount}</strong>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-slate-200">
                    <span className="text-slate-400 block text-[9px]">Conflictos</span>
                    <strong className="text-emerald-600 text-xs">{dryRunData.conflictsCount ?? 0}</strong>
                  </div>
                </div>
              </div>

              {/* Warning Notice */}
              <div className="p-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl text-xs space-y-1">
                <div className="flex items-center gap-1.5 font-black uppercase text-[11px] text-amber-800">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                  <span>Advertencia de Sobrescritura</span>
                </div>
                <p className="text-[11px] leading-relaxed text-amber-800">
                  Esta operación reemplazará la información actual del tenant por la información contenida en el resguardo seleccionado ({dryRunData.backupTimestamp || targetBackup.fechaHora}).
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2.5 pt-1">
                <button
                  type="button"
                  disabled={restoreState === 'RESTORE_LOADING'}
                  onClick={resetRestoreState}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 rounded-xl text-xs font-bold cursor-pointer transition-colors"
                >
                  CANCELAR
                </button>
                <button
                  type="button"
                  disabled={restoreState === 'RESTORE_LOADING'}
                  onClick={handleConfirmAndExecuteRestore}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 text-white rounded-xl text-xs font-black uppercase tracking-wider cursor-pointer transition-all shadow-md"
                >
                  {restoreState === 'RESTORE_LOADING' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Restaurando en Servidor...</span>
                    </>
                  ) : (
                    <span>CONTINUAR CON RESTAURACIÓN</span>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* OVERLAY: RESTORE ERROR MODAL */}
      <AnimatePresence>
        {restoreState === 'ERROR' && (
          <div className="fixed inset-0 z-55 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-xs font-sans">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-rose-200 shadow-2xl p-6 sm:p-7 max-w-md w-full space-y-4"
            >
              <div className="flex items-center gap-3 border-b border-rose-100 pb-3">
                <div className="p-2 bg-rose-100 text-rose-600 rounded-xl">
                  <ShieldAlert className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-rose-950 uppercase">
                    Fallo en la Validación / Restauración
                  </h3>
                  <p className="text-[10px] text-rose-600 font-mono">
                    ERROR DE OPERACIÓN SERVER-SIDE
                  </p>
                </div>
              </div>

              <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl space-y-1.5 text-xs text-rose-900">
                <div className="flex items-center justify-between">
                  <span className="font-bold uppercase text-[10px] text-rose-700">Código de Error:</span>
                  <span className="font-mono font-bold text-rose-800 bg-rose-100 px-1.5 py-0.5 rounded text-[10px]">
                    {errorCode}
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed pt-1">
                  {errorMessage}
                </p>
                {correlationId && (
                  <div className="pt-2 border-t border-rose-200/70 text-[10px] font-mono text-rose-600 flex items-center justify-between">
                    <span>Correlation ID:</span>
                    <span>{correlationId}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={resetRestoreState}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-950 text-white rounded-xl text-xs font-bold cursor-pointer transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* OVERLAY: RESTORE SUCCESS MODAL */}
      <AnimatePresence>
        {restoreState === 'SUCCESS' && restoreResultData && (
          <div className="fixed inset-0 z-55 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-xs font-sans">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-emerald-200 shadow-2xl p-6 sm:p-7 max-w-md w-full space-y-4"
            >
              <div className="flex items-center gap-3 border-b border-emerald-100 pb-3">
                <div className="p-2 bg-emerald-100 text-emerald-600 rounded-xl">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-emerald-950 uppercase">
                    ¡Restauración Server-Side Exitosa!
                  </h3>
                  <p className="text-[10px] text-emerald-600 font-mono">
                    TRANSACCIÓN ATÓMICA CONFIRMADA
                  </p>
                </div>
              </div>

              <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl space-y-2 text-xs text-emerald-900">
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <span className="text-emerald-700 text-[10px] block">Órdenes Restauradas:</span>
                    <strong className="text-sm text-emerald-950">{restoreResultData.restoredOrdenesCount}</strong>
                  </div>
                  <div>
                    <span className="text-emerald-700 text-[10px] block">Disposiciones:</span>
                    <strong className="text-sm text-emerald-950">{restoreResultData.restoredDisposicionesCount}</strong>
                  </div>
                </div>
                <div className="pt-2 border-t border-emerald-200 text-[10px] font-mono text-emerald-700">
                  <span>SHA-256 Verificado: {restoreResultData.verifiedSha256?.substring(0, 20)}...</span>
                </div>
                {correlationId && (
                  <div className="text-[9px] font-mono text-emerald-600">
                    <span>Audit ID: {correlationId}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={resetRestoreState}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold cursor-pointer transition-colors shadow-sm"
                >
                  Aceptar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* CONFIRMATION MODAL: Total System Reset */}
      <AnimatePresence>
        {showResetModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full space-y-6 shadow-2xl border border-rose-200"
            >
              <div className="flex items-center justify-between border-b border-rose-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-rose-100 text-rose-600 rounded-xl border border-rose-200">
                    <ShieldAlert className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-rose-950 uppercase tracking-tight">
                      Reestablecimiento Total del Sistema
                    </h3>
                    <p className="text-[10px] text-rose-700 font-mono">
                      AUTORIZACIÓN DE NIVEL TÉCNICO
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowResetModal(false)}
                  className="text-slate-400 hover:text-slate-600 p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-900 text-xs rounded-2xl leading-relaxed space-y-1">
                  <p className="font-bold">⚠️ Esta acción vaciará completamente:</p>
                  <ul className="list-disc list-inside text-[11px] space-y-0.5 text-rose-800">
                    <li>Todas las Órdenes de Producción ({ordenes.length} registros).</li>
                    <li>Todas las Disposiciones y Liberaciones ({disposiciones.length} registros).</li>
                    <li>Reiniciará el consecutivo oficial a N° 1.</li>
                  </ul>
                </div>

                {resetError && (
                  <div className="p-3 bg-rose-100 border border-rose-300 text-rose-800 text-xs rounded-xl font-sans font-bold flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0" />
                    <span>{resetError}</span>
                  </div>
                )}

                <div className="space-y-3 pt-1 font-sans">
                  <div>
                    <label className="block text-[11px] font-black text-slate-700 uppercase mb-1">
                      Escriba <span className="text-rose-600 select-all font-mono">REESTABLECER</span> en mayúsculas:
                    </label>
                    <input 
                      type="text"
                      value={resetConfirmationText}
                      onChange={(e) => setResetConfirmationText(e.target.value)}
                      placeholder="Escriba REESTABLECER aquí..."
                      className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500 font-mono font-bold uppercase"
                      id="input-reset-confirm-word"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowResetModal(false)}
                  className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleExecuteTotalReset}
                  className="flex-1 py-3 bg-rose-600 hover:bg-rose-700 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-md hover:shadow-lg"
                  id="btn-confirm-total-system-reset"
                >
                  Confirmar Reset Total
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
